import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  MapPin,
  Phone,
  Mail,
  Calendar,
  Tag,
  AlertCircle,
  BadgeCheck,
  UserCog,
  MessageCircle,
} from "lucide-react";
import API from "../services/api";
import { useAuth } from "../context/AuthContext";

const VoterDashboard = () => {
  const { isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    const fetchDashboard = async () => {
      try {
        const res = await API.get("/dashboard/profile");
        setProfile(res.data.user);
        setIssues(res.data.issues || []);
      } catch (err) {
        console.error("Dashboard error:", err);
        toast.error("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [authLoading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-100 via-white to-green-100">
        <div className="animate-pulse space-y-4 text-center">
          <div className="h-8 w-64 bg-gray-300 mx-auto rounded" />
          <div className="h-20 w-96 bg-gray-200 mx-auto rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-xl shadow-xl border-l-8 border-orange-500"
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            🇮🇳 Welcome, {profile?.name || "Voter"}!
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-700 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-600" />
              {profile?.permanentAddress?.addressLine1 || "No address"}
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              {profile?.phone}
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              {profile?.email}
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Issues Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="col-span-2"
          >
            <h2 className="text-2xl font-semibold mb-4 text-orange-700">
              📋 Your Raised Issues
            </h2>

            {issues.length === 0 ? (
              <div className="bg-white p-6 text-center rounded-xl shadow">
                <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No issues raised yet.</p>
                <Link
                  to="/raise-issue"
                  className="mt-4 inline-block bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
                >
                  🗣️ Raise Your First Issue
                </Link>
              </div>
            ) : (
              <div className="grid gap-4">
                {issues.map((issue) => (
                  <motion.div
                    key={issue._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-white p-4 rounded-lg shadow hover:shadow-md transition"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg text-gray-800">
                          {issue.title}
                        </h3>
                        <div className="flex gap-4 mt-2 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Tag className="w-4 h-4" />
                            {issue.category}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(issue.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          issue.status === "Resolved"
                            ? "bg-green-100 text-green-700"
                            : issue.status === "Under Review"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {issue.status || "Pending"}
                      </span>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Link
                        to={`/issues/${issue._id}`}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View Details →
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Right Sidebar - Tools */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Quick Actions */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4 text-orange-700">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Link
                  to="/raise-issue"
                  className="block w-full bg-green-600 text-white text-center py-2 rounded-lg hover:bg-green-700"
                >
                  ✋ Raise New Issue
                </Link>
                <Link
                  to="/explore-candidates"
                  className="block w-full bg-orange-500 text-white text-center py-2 rounded-lg hover:bg-orange-600"
                >
                  👥 Explore Candidates
                </Link>
                <Link
                  to="/my-constituency"
                  className="block w-full bg-blue-600 text-white text-center py-2 rounded-lg hover:bg-blue-700"
                >
                  🏩 My Constituency
                </Link>
              </div>
            </div>

            {/* Tools for Participation */}
            <div className="bg-white p-6 rounded-lg shadow space-y-4">
              <h3 className="text-xl font-semibold text-green-700">
                Voter Tools
              </h3>
              <Link
                to="/badges"
                className="flex items-center gap-2 text-sm text-green-700 hover:underline"
              >
                <BadgeCheck size={18} />
                My Badges & Impact
              </Link>
              <Link
                to="/edit-profile"
                className="flex items-center gap-2 text-sm text-gray-700 hover:underline"
              >
                <UserCog size={18} />
                Edit My Profile
              </Link>
              <Link
                to="/discussions"
                className="flex items-center gap-2 text-sm text-purple-700 hover:underline"
              >
                <MessageCircle size={18} />
                Join Public Discussions
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VoterDashboard;
